import { useEffect, useState } from "react";
import { BadgeCheck, MessageSquare } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Btn, Field, inputCls } from "@/components/Primitives";
import { SummitFormActions, SummitFormHeader, SummitFormModal } from "@/components/forms/SummitForm";

const REVIEW_CATEGORIES = [
  ["itinerary_quality", "Itinerary"],
  ["guide_quality", "Guide"],
  ["organization_quality", "Organization"],
  ["safety", "Safety"],
  ["communication", "Communication"],
  ["accommodation", "Accommodation"],
  ["transportation", "Transportation"],
  ["value_for_money", "Value"],
  ["group_experience", "Group experience"],
  ["route_accuracy", "Route accuracy"],
];

const emptyReview = {
  overall_rating: 5,
  title: "",
  review_text: "",
  testimonial_text: "",
  would_recommend: true,
  would_join_again: true,
  testimonial_consent: false,
  display_name_preference: "limited",
  photo_consent: false,
  visibility: "organization",
  private_feedback: "",
  category_ratings: Object.fromEntries(REVIEW_CATEGORIES.map(([key]) => [key, 5])),
};

function reviewFormFrom(review) {
  if (!review) return emptyReview;
  return {
    ...emptyReview,
    ...review,
    category_ratings: { ...emptyReview.category_ratings, ...(review.category_ratings || {}) },
  };
}

function reviewPayloadFrom(form) {
  return {
    overall_rating: Number(form.overall_rating),
    title: form.title || "",
    review_text: form.review_text || "",
    testimonial_text: form.testimonial_text || "",
    would_recommend: !!form.would_recommend,
    would_join_again: !!form.would_join_again,
    testimonial_consent: !!form.testimonial_consent,
    display_name_preference: form.display_name_preference,
    photo_consent: !!form.photo_consent,
    visibility: form.visibility,
    private_feedback: form.private_feedback || "",
    category_ratings: Object.fromEntries(
      REVIEW_CATEGORIES.map(([key]) => [key, Number(form.category_ratings?.[key] || 5)])
    ),
  };
}

function ReviewSummary({ title, summary, unavailable, focusCategory, focusLabel }) {
  if (unavailable) {
    return (
      <div className="rounded-2xl border border-dashed border-[#E5B85C]/45 bg-[#FFFAF1]/75 p-3 text-sm text-slate-500">
        <p className="font-semibold text-[#17211D]">{title}</p>
        <p className="mt-1">{unavailable}</p>
      </div>
    );
  }
  if (!summary) return null;
  const focusRating = focusCategory ? summary.category_ratings?.[focusCategory] : null;
  return (
    <div className="rounded-2xl bg-[#F7F1E5] p-3 text-sm">
      <p className="font-semibold text-[#17211D]">{title}</p>
      <p className="mt-1 text-slate-600">{summary.average_rating || 0} average from {summary.review_count || 0} reviews</p>
      {focusRating && <p className="mt-1 text-xs font-semibold text-[#2D7A65]">{focusLabel}: {focusRating}/5</p>}
      <p className="mt-1 text-xs text-slate-500">{summary.would_recommend_pct || 0}% recommend - {summary.would_join_again_pct || 0}% would join again</p>
    </div>
  );
}

export default function ParticipantJourneyReview({
  tripId,
  trip,
  participant,
  review,
  reviewError,
  publicSummary,
  guideSummary,
  guideSummaryError,
  onSaved,
}) {
  const [form, setForm] = useState(reviewFormFrom(review));
  const [saving, setSaving] = useState(false);
  const [formOpen, setFormOpen] = useState(false);

  useEffect(() => { setForm(reviewFormFrom(review)); }, [review]);

  const status = String(participant?.status || "").toLowerCase();
  const eligible = trip?.state === "completed" && status === "accepted" && !participant?.removed_at;
  const hasReview = !!review?.id;
  const setField = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));
  const setCategory = (key, value) => setForm((prev) => ({ ...prev, category_ratings: { ...prev.category_ratings, [key]: Number(value) } }));

  const submit = async (e) => {
    e.preventDefault();
    if (!eligible) return toast.error("Reviews are available only after eligible completed trips.");
    setSaving(true);
    try {
      const payload = reviewPayloadFrom(form);
      const { data } = hasReview
        ? await api.put(`/my/trips/${tripId}/review`, payload)
        : await api.post(`/my/trips/${tripId}/review`, payload);
      toast.success(hasReview ? "Review updated" : "Review submitted");
      onSaved(data);
      setFormOpen(false);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="tp-paper mb-6 rounded-[2rem] p-6" data-testid="participant-review-section">
      <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="flex items-center gap-2 text-base font-semibold"><MessageSquare className="h-4 w-4 text-[#2D7A65]" /> Review and feedback</h3>
          <p className="mt-1 text-sm text-slate-500">Private feedback stays on the participant-owned review record and is not part of public testimonials.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {review?.verified_participant_badge && <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-3 py-1 text-xs font-bold text-emerald-700"><BadgeCheck className="h-3.5 w-3.5" /> Verified participant</span>}
          {eligible && <Btn type="button" variant="secondary" onClick={() => setFormOpen(true)}>{hasReview ? "Edit review" : "Write review"}</Btn>}
        </div>
      </div>

      <div className="mb-4 grid gap-3 sm:grid-cols-3">
        <ReviewSummary title="Organization rating summary" summary={publicSummary} unavailable={!publicSummary ? "Public organization summary is unavailable for this trip." : ""} focusCategory="organization_quality" focusLabel="Organization" />
        <ReviewSummary title="Guide rating summary" summary={guideSummary} unavailable={guideSummaryError || (!guideSummary ? "Guide dashboard summary is unavailable to this participant." : "")} focusCategory="guide_quality" focusLabel="Guide" />
        <div className="rounded-2xl bg-[#F7F1E5] p-3 text-sm">
          <p className="font-semibold text-[#17211D]">Review status</p>
          <p className="mt-1 text-slate-600">{hasReview ? (review.moderation_status || "pending") : eligible ? "Not submitted" : "Not eligible yet"}</p>
          {reviewError && <p className="mt-1 text-xs text-rose-600">{reviewError}</p>}
        </div>
      </div>

      {!eligible ? (
        <p className="rounded-xl border border-dashed border-[#E5B85C]/40 p-4 text-sm text-slate-500">Reviews open only after the trip is completed for accepted participants who are not cancelled or removed.</p>
      ) : formOpen ? (
        <SummitFormModal onClose={() => setFormOpen(false)} label={hasReview ? "Edit your review" : "Share your review"} className="max-w-5xl">
        <form onSubmit={submit} className="summit-form summit-form--sheet">
          <SummitFormHeader compact eyebrow="Trip reflection" title={hasReview ? "Edit your review" : "Share your review"} description="Your private feedback stays with the organizer; public reviews are moderated." onClose={() => setFormOpen(false)} />
          <div className="summit-form__body space-y-4">
          <div className="grid gap-4 sm:grid-cols-3">
            <Field label="Overall rating">
              <select className={inputCls} value={form.overall_rating} onChange={(e) => setField("overall_rating", Number(e.target.value))}>
                {[5, 4, 3, 2, 1].map((value) => <option key={value} value={value}>{value} stars</option>)}
              </select>
            </Field>
            <Field label="Visibility">
              <select className={inputCls} value={form.visibility} onChange={(e) => setField("visibility", e.target.value)}>
                <option value="private">Private</option>
                <option value="organization">Organization only</option>
                <option value="public">Public after moderation</option>
              </select>
            </Field>
            <Field label="Display name">
              <select className={inputCls} value={form.display_name_preference} onChange={(e) => setField("display_name_preference", e.target.value)}>
                <option value="limited">First name</option>
                <option value="full">Full name</option>
                <option value="anonymous">Anonymous verified participant</option>
              </select>
            </Field>
          </div>
          <Field label="Review title"><input className={inputCls} value={form.title} onChange={(e) => setField("title", e.target.value)} maxLength={160} /></Field>
          <Field label="Review text"><textarea className={`${inputCls} min-h-28`} value={form.review_text} onChange={(e) => setField("review_text", e.target.value)} maxLength={4000} /></Field>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
            {REVIEW_CATEGORIES.map(([key, label]) => (
              <Field key={key} label={label}>
                <select className={inputCls} value={form.category_ratings[key] || 5} onChange={(e) => setCategory(key, e.target.value)}>
                  {[5, 4, 3, 2, 1].map((value) => <option key={value} value={value}>{value}</option>)}
                </select>
              </Field>
            ))}
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <label className="flex min-h-10 items-center gap-2 rounded-2xl bg-[#F7F1E5] p-3 text-sm font-semibold"><input type="checkbox" checked={!!form.would_recommend} onChange={(e) => setField("would_recommend", e.target.checked)} /> Would recommend</label>
            <label className="flex min-h-10 items-center gap-2 rounded-2xl bg-[#F7F1E5] p-3 text-sm font-semibold"><input type="checkbox" checked={!!form.would_join_again} onChange={(e) => setField("would_join_again", e.target.checked)} /> Would join again</label>
          </div>
          <Field label="Testimonial text"><textarea className={`${inputCls} min-h-24`} value={form.testimonial_text} onChange={(e) => setField("testimonial_text", e.target.value)} maxLength={1000} /></Field>
          <div className="grid gap-3 sm:grid-cols-2">
            <label className="flex min-h-10 items-center gap-2 rounded-2xl bg-[#F7F1E5] p-3 text-sm font-semibold"><input type="checkbox" checked={!!form.testimonial_consent} onChange={(e) => setField("testimonial_consent", e.target.checked)} /> I consent to testimonial use</label>
            <label className="flex min-h-10 items-center gap-2 rounded-2xl bg-[#F7F1E5] p-3 text-sm font-semibold"><input type="checkbox" checked={!!form.photo_consent} onChange={(e) => setField("photo_consent", e.target.checked)} /> I consent to photo use</label>
          </div>
          <Field label="Private feedback"><textarea className={`${inputCls} min-h-28`} value={form.private_feedback} onChange={(e) => setField("private_feedback", e.target.value)} maxLength={4000} /></Field>
          {review?.guide_response?.response_text && (
            <div className="rounded-2xl bg-sky-50 p-3 text-sm text-sky-800">
              <p className="font-semibold">Trip response</p>
              <p className="mt-1">{review.guide_response.response_text}</p>
            </div>
          )}
          </div>
          <SummitFormActions><Btn type="button" variant="secondary" onClick={() => setFormOpen(false)}>Cancel</Btn><Btn type="submit" disabled={saving}>{saving ? "Saving..." : hasReview ? "Update review" : "Submit review"}</Btn></SummitFormActions>
        </form>
        </SummitFormModal>
      ) : (
        <p className="rounded-xl border border-dashed border-[#E5B85C]/40 p-4 text-sm text-slate-500">{hasReview ? "Your review is saved. Open it to make changes." : "Share a review when you are ready."}</p>
      )}
    </section>
  );
}
